// Interactive onboarding walkthrough
// Highlights UI elements one at a time with a box-shadow cutout

import './Walkthrough.css'
import { t } from '@/i18n'
import { isEditMode } from '@/utils/editMode'
import { isDemoMode } from '@/utils/demoMode'
import { getLeftPanel } from './state'
import type { LayerTab } from './LeftPanel'

export interface WalkthroughStep {
  target: string | (() => Element | null) | null
  titleKey: string
  descKey: string
  position?: 'top' | 'bottom' | 'left' | 'right'
  condition?: () => boolean
  onEnter?: () => void
  onLeave?: () => void
}

export type WalkthroughPreset = 'general' | 'plants' | 'architecture' | 'ground' | 'metadata' | 'map'

const PADDING = 8
const STORAGE_KEY = 'garden-editor-walkthrough-seen'

/**
 * Capture left panel state and return onEnter/onLeave helpers
 * for contextual (per-tab) presets that need to expand a specific tab.
 */
function panelHelpers(tab: LayerTab) {
  const leftPanel = getLeftPanel()
  const wasOpen = leftPanel?.isOpen() ?? false
  const previousTab = leftPanel?.getActiveTab() ?? 'plants'

  return {
    onEnter: () => {
      const lp = getLeftPanel()
      if (lp) lp.expand(tab)
    },
    onLeave: () => {
      const lp = getLeftPanel()
      if (!lp) return
      if (wasOpen) {
        lp.expand(previousTab)
      } else {
        lp.collapse()
      }
    },
  }
}

function defineGeneralSteps(): WalkthroughStep[] {
  const leftPanel = getLeftPanel()
  const wasOpen = leftPanel?.isOpen() ?? false
  const previousTab = leftPanel?.getActiveTab() ?? 'plants'

  return [
    {
      target: null,
      titleKey: 'walkthrough.welcome.title',
      descKey: 'walkthrough.welcome.desc',
    },
    {
      target: '#canvas-container',
      titleKey: 'walkthrough.canvas.title',
      descKey: 'walkthrough.canvas.desc',
      position: 'top',
    },
    {
      target: () => document.querySelector('#top-bar-2d-btn')?.parentElement ?? null,
      titleKey: 'walkthrough.viewModes.title',
      descKey: 'walkthrough.viewModes.desc',
      position: 'bottom',
    },
    {
      target: '.top-bar-layer-group',
      titleKey: 'walkthrough.layers.title',
      descKey: 'walkthrough.layers.desc',
      position: 'bottom',
    },
    {
      target: () => document.querySelector('.left-panel'),
      titleKey: 'walkthrough.leftPanel.title',
      descKey: 'walkthrough.leftPanel.desc',
      position: 'right',
      condition: () => isEditMode(),
      onEnter: () => {
        const lp = getLeftPanel()
        if (lp) lp.expand('plants')
      },
      onLeave: () => {
        const lp = getLeftPanel()
        if (!lp) return
        if (wasOpen) {
          lp.expand(previousTab)
        } else {
          lp.collapse()
        }
      },
    },
    {
      target: '#top-bar-file-ops-group',
      titleKey: 'walkthrough.fileOps.title',
      descKey: 'walkthrough.fileOps.desc',
      position: 'bottom',
      condition: () => {
        if (!isEditMode()) return false
        if (isDemoMode()) return false
        const el = document.getElementById('top-bar-file-ops-group')
        return el ? el.style.display !== 'none' : false
      },
    },
    {
      target: () => document.querySelector('#top-bar-undo-group'),
      titleKey: 'walkthrough.editActions.title',
      descKey: 'walkthrough.editActions.desc',
      position: 'bottom',
      condition: () => {
        if (!isEditMode()) return false
        const el = document.getElementById('top-bar-undo-group')
        return el ? el.style.display !== 'none' : false
      },
    },
    {
      target: '.garden-name-wrapper',
      titleKey: 'walkthrough.gardenName.title',
      descKey: 'walkthrough.gardenName.desc',
      position: 'bottom',
      condition: () => isEditMode(),
    },
    {
      target: '#canvas-controls',
      titleKey: 'walkthrough.canvasControls.title',
      descKey: 'walkthrough.canvasControls.desc',
      position: 'left',
    },
    {
      target: '#bottom-bar',
      titleKey: 'walkthrough.bottomBar.title',
      descKey: 'walkthrough.bottomBar.desc',
      position: 'top',
    },
    {
      target: '#top-bar-settings-btn',
      titleKey: 'walkthrough.settings.title',
      descKey: 'walkthrough.settings.desc',
      position: 'bottom',
    },
    {
      target: '#top-bar-help-btn',
      titleKey: 'walkthrough.helpBtn.title',
      descKey: 'walkthrough.helpBtn.desc',
      position: 'bottom',
    },
  ]
}

function defineProductTabSteps(tab: 'plants' | 'architecture' | 'ground'): WalkthroughStep[] {
  const helpers = panelHelpers(tab)

  // CSS class prefixes match the tab name
  const prefix = tab === 'ground' ? 'ground' : tab

  return [
    {
      target: `.${prefix}-product-browser .product-search`,
      titleKey: `walkthrough.${tab}.search.title`,
      descKey: `walkthrough.${tab}.search.desc`,
      position: 'right',
      onEnter: helpers.onEnter,
    },
    {
      target: `.${prefix}-product-browser .product-category-bar`,
      titleKey: `walkthrough.${tab}.categories.title`,
      descKey: `walkthrough.${tab}.categories.desc`,
      position: 'right',
    },
    {
      target: `.${prefix}-product-browser .product-grid`,
      titleKey: `walkthrough.${tab}.grid.title`,
      descKey: `walkthrough.${tab}.grid.desc`,
      position: 'right',
    },
    {
      target: `.${prefix}-hint-bar`,
      titleKey: `walkthrough.${tab}.hint.title`,
      descKey: `walkthrough.${tab}.hint.desc`,
      position: 'right',
      onLeave: helpers.onLeave,
    },
  ]
}

function defineMetadataSteps(): WalkthroughStep[] {
  const helpers = panelHelpers('metadata')

  return [
    {
      target: '.metadata-category-tabs',
      titleKey: 'walkthrough.metadata.categories.title',
      descKey: 'walkthrough.metadata.categories.desc',
      position: 'right',
      onEnter: helpers.onEnter,
    },
    {
      target: '.metadata-zone-grid',
      titleKey: 'walkthrough.metadata.zones.title',
      descKey: 'walkthrough.metadata.zones.desc',
      position: 'right',
    },
    {
      target: '.metadata-poi-library',
      titleKey: 'walkthrough.metadata.poi.title',
      descKey: 'walkthrough.metadata.poi.desc',
      position: 'right',
    },
    {
      target: '.metadata-hint-bar',
      titleKey: 'walkthrough.metadata.hint.title',
      descKey: 'walkthrough.metadata.hint.desc',
      position: 'right',
      onLeave: helpers.onLeave,
    },
  ]
}

function defineMapSteps(): WalkthroughStep[] {
  const helpers = panelHelpers('map')

  return [
    {
      target: () => document.querySelector('.map-tab-hero') ?? document.querySelector('.map-tab-success-header'),
      titleKey: 'walkthrough.map.import.title',
      descKey: 'walkthrough.map.import.desc',
      position: 'right',
      onEnter: helpers.onEnter,
    },
    {
      target: '.map-tab-actions',
      titleKey: 'walkthrough.map.actions.title',
      descKey: 'walkthrough.map.actions.desc',
      position: 'right',
    },
    {
      target: () => document.querySelector('.map-tab-hint'),
      titleKey: 'walkthrough.map.hint.title',
      descKey: 'walkthrough.map.hint.desc',
      position: 'right',
      onLeave: helpers.onLeave,
    },
  ]
}

const PRESET_FACTORIES: Record<WalkthroughPreset, () => WalkthroughStep[]> = {
  general: defineGeneralSteps,
  plants: () => defineProductTabSteps('plants'),
  architecture: () => defineProductTabSteps('architecture'),
  ground: () => defineProductTabSteps('ground'),
  metadata: defineMetadataSteps,
  map: defineMapSteps,
}

class Walkthrough {
  private steps: WalkthroughStep[] = []
  private currentIndex = 0
  private isActive = false

  // DOM elements
  private overlay: HTMLDivElement | null = null
  private highlight: HTMLDivElement | null = null
  private tooltip: HTMLDivElement | null = null

  // Bound listeners
  private boundKeydown = this.handleKeydown.bind(this)
  private boundResize = this.handleResize.bind(this)
  private rafId = 0

  start(steps: WalkthroughStep[]): void {
    if (this.isActive) return
    this.isActive = true
    this.steps = steps
    this.currentIndex = 0

    // Create DOM
    this.overlay = document.createElement('div')
    this.overlay.className = 'walkthrough-overlay'
    this.overlay.addEventListener('click', () => this.next())

    this.highlight = document.createElement('div')
    this.highlight.className = 'walkthrough-highlight'

    this.tooltip = document.createElement('div')
    this.tooltip.className = 'walkthrough-tooltip'

    document.body.appendChild(this.overlay)
    document.body.appendChild(this.highlight)
    document.body.appendChild(this.tooltip)

    // Listeners
    document.addEventListener('keydown', this.boundKeydown)
    window.addEventListener('resize', this.boundResize)

    this.showStep(this.currentIndex)
  }

  exit(): void {
    if (!this.isActive) return

    // Fire onLeave for current step
    const currentStep = this.steps[this.currentIndex]
    if (currentStep?.onLeave) currentStep.onLeave()

    this.isActive = false

    // Mark walkthrough as seen so it won't auto-run again
    try { localStorage.setItem(STORAGE_KEY, '1') } catch { /* private browsing */ }

    this.overlay?.remove()
    this.highlight?.remove()
    this.tooltip?.remove()
    this.overlay = null
    this.highlight = null
    this.tooltip = null

    document.removeEventListener('keydown', this.boundKeydown)
    window.removeEventListener('resize', this.boundResize)
    if (this.rafId) cancelAnimationFrame(this.rafId)
  }

  private next(): void {
    // Fire onLeave for current step
    const currentStep = this.steps[this.currentIndex]
    if (currentStep?.onLeave) currentStep.onLeave()

    let idx = this.currentIndex + 1
    while (idx < this.steps.length && this.steps[idx].condition && !this.steps[idx].condition!()) {
      idx++
    }
    if (idx >= this.steps.length) {
      this.exit()
      return
    }
    this.currentIndex = idx
    this.showStep(idx)
  }

  private prev(): void {
    // Fire onLeave for current step
    const currentStep = this.steps[this.currentIndex]
    if (currentStep?.onLeave) currentStep.onLeave()

    let idx = this.currentIndex - 1
    while (idx >= 0 && this.steps[idx].condition && !this.steps[idx].condition!()) {
      idx--
    }
    if (idx < 0) return
    this.currentIndex = idx
    this.showStep(idx)
  }

  private showStep(index: number): void {
    const step = this.steps[index]
    if (!step || !this.highlight || !this.tooltip) return

    // Skip steps whose condition fails
    if (step.condition && !step.condition()) {
      this.next()
      return
    }

    // Fire onEnter for this step
    if (step.onEnter) step.onEnter()

    // Resolve target element (after onEnter, so DOM changes are applied)
    const targetEl = this.resolveTarget(step)

    // Position highlight
    if (!targetEl) {
      // No target — centered welcome step, hide highlight visually but keep shadow
      this.highlight.classList.add('hidden')
      this.highlight.style.top = '50%'
      this.highlight.style.left = '50%'
      this.highlight.style.width = '0px'
      this.highlight.style.height = '0px'
    } else {
      this.highlight.classList.remove('hidden')
      const rect = targetEl.getBoundingClientRect()
      this.highlight.style.top = `${rect.top - PADDING}px`
      this.highlight.style.left = `${rect.left - PADDING}px`
      this.highlight.style.width = `${rect.width + PADDING * 2}px`
      this.highlight.style.height = `${rect.height + PADDING * 2}px`
    }

    // Count visible steps for counter
    const visibleSteps = this.steps.filter(s => !s.condition || s.condition())
    const visibleIndex = visibleSteps.indexOf(step)
    const isLast = index === this.findLastVisibleIndex()
    const isFirst = index === this.findFirstVisibleIndex()

    // Render tooltip content
    this.tooltip.innerHTML = ''

    const counter = document.createElement('div')
    counter.className = 'walkthrough-step-counter'
    counter.textContent = `${visibleIndex + 1} / ${visibleSteps.length}`

    const title = document.createElement('div')
    title.className = 'walkthrough-step-title'
    title.textContent = t(step.titleKey)

    const desc = document.createElement('p')
    desc.className = 'walkthrough-step-desc'
    desc.textContent = t(step.descKey)

    const nav = document.createElement('div')
    nav.className = 'walkthrough-step-nav'

    const skipBtn = document.createElement('button')
    skipBtn.className = 'walkthrough-btn-skip'
    skipBtn.textContent = t('walkthrough.skip')
    skipBtn.addEventListener('click', (e) => { e.stopPropagation(); this.exit() })

    const navRight = document.createElement('div')
    navRight.className = 'walkthrough-nav-right'

    if (!isFirst) {
      const prevBtn = document.createElement('button')
      prevBtn.className = 'walkthrough-btn-prev'
      prevBtn.textContent = t('walkthrough.prev')
      prevBtn.addEventListener('click', (e) => { e.stopPropagation(); this.prev() })
      navRight.appendChild(prevBtn)
    }

    const nextBtn = document.createElement('button')
    nextBtn.className = 'walkthrough-btn-primary'
    nextBtn.textContent = isLast ? t('walkthrough.finish') : t('walkthrough.next')
    nextBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      if (isLast) this.exit()
      else this.next()
    })
    navRight.appendChild(nextBtn)

    nav.appendChild(skipBtn)
    nav.appendChild(navRight)

    this.tooltip.appendChild(counter)
    this.tooltip.appendChild(title)
    this.tooltip.appendChild(desc)
    this.tooltip.appendChild(nav)

    // Position tooltip
    if (!targetEl) {
      // Centered
      this.tooltip.style.top = '50%'
      this.tooltip.style.left = '50%'
      this.tooltip.style.transform = 'translate(-50%, -50%)'
    } else {
      this.tooltip.style.transform = ''
      const targetRect = targetEl.getBoundingClientRect()
      this.positionTooltip(targetRect, step.position || 'bottom')
    }
  }

  private positionTooltip(targetRect: DOMRect, position: 'top' | 'bottom' | 'left' | 'right'): void {
    if (!this.tooltip) return

    const gap = 12
    const tooltipWidth = 340
    // Temporarily make visible off-screen to measure height
    this.tooltip.style.left = '-9999px'
    this.tooltip.style.top = '0'
    const tooltipHeight = this.tooltip.offsetHeight

    let top = 0
    let left = 0

    switch (position) {
      case 'bottom':
        top = targetRect.bottom + PADDING + gap
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2
        break
      case 'top':
        top = targetRect.top - PADDING - gap - tooltipHeight
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2
        break
      case 'right':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2
        left = targetRect.right + PADDING + gap
        break
      case 'left':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2
        left = targetRect.left - PADDING - gap - tooltipWidth
        break
    }

    // Viewport bounds checking
    const margin = 16
    if (left < margin) left = margin
    if (left + tooltipWidth > window.innerWidth - margin) left = window.innerWidth - margin - tooltipWidth
    if (top < margin) top = margin
    if (top + tooltipHeight > window.innerHeight - margin) top = window.innerHeight - margin - tooltipHeight

    this.tooltip.style.top = `${top}px`
    this.tooltip.style.left = `${left}px`
  }

  private resolveTarget(step: WalkthroughStep): Element | null {
    if (!step.target) return null

    if (typeof step.target === 'function') {
      return step.target()
    }

    return document.querySelector(step.target)
  }

  private findFirstVisibleIndex(): number {
    for (let i = 0; i < this.steps.length; i++) {
      if (!this.steps[i].condition || this.steps[i].condition!()) return i
    }
    return 0
  }

  private findLastVisibleIndex(): number {
    for (let i = this.steps.length - 1; i >= 0; i--) {
      if (!this.steps[i].condition || this.steps[i].condition!()) return i
    }
    return this.steps.length - 1
  }

  private handleKeydown(e: KeyboardEvent): void {
    switch (e.key) {
      case 'ArrowRight':
      case 'Enter':
        e.preventDefault()
        e.stopPropagation()
        this.next()
        break
      case 'ArrowLeft':
        e.preventDefault()
        e.stopPropagation()
        this.prev()
        break
      case 'Escape':
        e.preventDefault()
        e.stopPropagation()
        this.exit()
        break
    }
  }

  private handleResize(): void {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    this.rafId = requestAnimationFrame(() => {
      if (this.isActive) this.showStep(this.currentIndex)
    })
  }
}

let instance: Walkthrough | null = null

export function startWalkthrough(preset?: WalkthroughPreset | WalkthroughStep[]): void {
  if (!instance) instance = new Walkthrough()

  let steps: WalkthroughStep[]
  if (Array.isArray(preset)) {
    steps = preset
  } else {
    const key = preset ?? 'general'
    steps = PRESET_FACTORIES[key]()
  }

  instance.start(steps)
}

/**
 * Auto-run the general walkthrough on first visit.
 * Call this after the UI is fully initialized.
 */
export function autoStartWalkthroughIfNew(): void {
  try {
    if (localStorage.getItem(STORAGE_KEY)) return
  } catch {
    return // private browsing — don't show
  }
  startWalkthrough('general')
}

export { Walkthrough }
